import { makeObservable, observable, action, runInAction } from 'mobx';
import { interval, Subscription } from 'rxjs';
import { map, take } from 'rxjs/operators';
import { checkEmail, checkPassword } from '@utils/dataCheck';
import { userAPI } from '@api/User.api';
import { rootStore, RootStore } from './Root.store';
import { IUserSettings } from '@api/User.api';
import { groupsAPI } from '@api/Groups.api';

export default class UserStore {
    @observable
    public authError: boolean;

    @observable
    public confirmError: boolean;

    @observable
    public confirmed: boolean;

    @observable
    public passwordConfirmed: boolean;

    @observable
    public email: string | null;

    @observable
    public type: string;

    @observable
    public token: string | null;

    @observable
    public loading: boolean;

    @observable
    public notExistError: boolean;

    @observable
    public sendedAgain: boolean;

    @observable
    public serverError: boolean;

    @observable
    public registerError: boolean;
    public settings: Partial<IUserSettings>;

    @observable
    public changeSettingsSuccess: boolean;

    @observable
    public isAllowedToRequestAgain: boolean;

    @observable
    public timeToRequestAgain: number;

    @observable
    public timer: Subscription | null;

    @observable
    public registerLoading: boolean;

    @observable
    public confirmLoading: boolean;

    @observable
    public changeSettingsLoading: boolean;

    @observable
    public requestConfirmLoading: boolean;

    @observable
    public passwordRecoveryLoading: boolean;

    @observable
    public confirmPasswordRecoveryLoading: boolean;

    @observable
    public operationSuccess: boolean;

    @observable
    public operationSuccessOldPass: boolean;

    @observable
    public userLogin: string;

    @observable
    public confirmationErr: boolean;

    @observable
    public timeOut = 60;

    @observable
    public oldDevice: false;

    @observable
    public imeiOld: string;
    
    @observable
    public loginOldActivated: boolean;

    @observable
    public loginActive: boolean;

    @observable
    public groupNameOldlogin: string;

    constructor(private rootStore: RootStore) {
        this.setDefault();
        makeObservable(this);
    }

    @action
    setDefault() {
        this.oldDevice = false;
        this.loginActive = false;
        this.loginOldActivated = false;
        this.loading = false;
        this.authError = false;
        this.confirmError = false;
        this.email = '';
        this.token = localStorage.getItem('token') ?? '';
        this.timeToRequestAgain = this.timeOut;
        this.isAllowedToRequestAgain = false;
        this.sendedAgain = false;
        this.confirmed = false;
        this.confirmationErr = false;
        this.settings = {};
        this.changeSettingsSuccess = false;
        this.changeSettingsLoading = false;
        this.registerLoading = false;
        this.confirmLoading = false;
        this.requestConfirmLoading = false;
        this.passwordRecoveryLoading = false;
        this.confirmPasswordRecoveryLoading = false;
        this.passwordConfirmed = false;
        this.serverError = false;
        this.registerError = false;
        this.notExistError = false;
        this.timer = null;
        this.type = localStorage.getItem('userType') ?? '';
        this.operationSuccess = false;
        this.operationSuccessOldPass = false;
        this.userLogin = localStorage.getItem('userLogin') ?? '';
    }

    @action
    _checkAuth(email: string, password: string) {
        const isEmailValid = checkEmail(email);
        const isPasswordValid = checkPassword(password);
        return isEmailValid && isPasswordValid;
    }

    @action
    startTimer() {
        this.stopTimer();
        const seconds = this.timeOut;
        this.isAllowedToRequestAgain = false;
        this.timeToRequestAgain = seconds;
        const stream = interval(1000).pipe(
            map((time) => seconds - time),
            take(seconds + 1),
        );
        this.timer = stream.subscribe({
            next: (time) => {
                runInAction(() => {
                    this.timeToRequestAgain = time;
                });
            },
            complete: () => {
                runInAction(() => {
                    this.isAllowedToRequestAgain = true;
                });
            },
        });
    }

    @action
    stopTimer() {
        if (this.timer) {
            this.timer.unsubscribe();
            this.timeToRequestAgain = this.timeOut;
            this.isAllowedToRequestAgain = false;
        }
    }

    @action
    async login(login: string, password: string) {
        const isLoginValid = !!login;
        const isPasswordValid = checkPassword(password);
        const isDataValid = isLoginValid && isPasswordValid;        
        if (isDataValid) {
            this.loading = true;
            try {
                const res = await userAPI.login(login, password);
                if (res.result === 'success') {
                    runInAction(() => {
                        this.loading = false;
                        this.token = res.token;
                        this.type = res.type;
                        this.email = login;
                        this.authError = false;
                        this.setUserLogin(login);
                        localStorage.setItem('token', this.token);
                        localStorage.setItem('userType', this.type);
                    });
                } else if (
                    res.result === 'error' &&
                    res.info.indexOf('not_confirmed') >= 0
                ) {
                    runInAction(() => {
                        this.loading = false;
                        this.authError = false;
                        this.email = login;
                    });
                } else if (
                    res.result === 'error' &&
                    (res.info.indexOf('not_exists') >= 0 ||
                        res.info.indexOf('not_exists_user') >= 0)
                ) {
                    runInAction(() => {
                        this.loading = false;
                        this.authError = true;
                    });
                } else {
                    runInAction(() => {
                        this.loading = false;
                        this.serverError = true;
                    });
                }
            } catch (e) {
                runInAction(() => {
                    this.loading = false;
                    this.serverError = true;
                });
            }
        }
    }

    @action
    async loginOldAccount(login: string, password: string) {
        const token = this.token!;
        const isLoginValid = !!login;
        const isPasswordValid = checkPassword(password);
        const isDataValid = isLoginValid && isPasswordValid;
        if (isDataValid) {
            this.loading = true;
            try{
                const res = await userAPI.loginOldAccount(login, password, token);                                                                            
                if (res.result === 'success') {    
                    runInAction(() => {
                        this.loading = false;
                        this.authError = false;
                        this.operationSuccessOldPass = true;      
                    })                    
                }
                if (res.result === 'error') {
                    runInAction(() => {
                        this.loading = false;
                        this.authError = true;
                    })
                }
                if (res.result === 'error' && res.info[0] === 'already_exists') {
                    runInAction(() => {
                        this.loginActive = true;
                        this.loading = false;
                        this.authError = false;
                        this.operationSuccessOldPass = true; 
                    })
                }
            }
            catch (e) {
                runInAction(() => {
                    this.loading = false;
                    this.serverError = true;
                });
            }
        }        
    }

    @action
    async logout() {
        const email = this.userLogin;
        const token = this.token;
        if (!email || !token) return;
        try {
            this.loading = true;
            const res = await userAPI.logout(email, token);
            if (res.result === 'success') {
                runInAction(() => {
                    this.rootStore.app.resetDemoMode();
                    this.loading = false;
                    this.registerError = false;
                    this.email = null;
                    this.token = '';
                    this.type = '';
                    this.userLogin = '';
                    localStorage.setItem('token', this.token);
                    localStorage.setItem('userType', this.type);
                });
            } else {
                runInAction(() => {
                    this.loading = false;
                    this.serverError = true;
                });
            }
        } catch (e) {
            runInAction(() => {
                this.loading = false;
                this.serverError = true;
            });
        }
    }

    @action
    async register(email: string, password: string) {
        const isDataValid = this._checkAuth(email, password);
        if (isDataValid) {
            this.registerLoading = true;
            try {
                const res = await userAPI.register(email, password);
                if (res.result === 'success') {
                    runInAction(() => {
                        this.registerLoading = false;
                        this.registerError = false;
                        this.email = email;
                    });
                } else if (
                    res.result === 'error' &&
                    res.info.indexOf('not_confirmed') >= 0
                ) {
                    runInAction(() => {
                        this.registerLoading = false;
                        this.email = email;
                        this.requestConfirm();
                    });
                } else if (
                    res.result === 'error' &&
                    res.info.indexOf('already_exists') >= 0
                ) {
                    runInAction(() => {
                        this.registerLoading = false;
                        this.registerError = true;
                    });
                } else {
                    runInAction(() => {
                        this.registerLoading = false;
                        this.serverError = true;
                    });
                }
            } catch (e) {
                runInAction(() => {
                    this.registerLoading = false;
                    this.serverError = true;
                });
            }
        }
    }

    @action
    async confirm(code: string) {
        const email = this.email;
        if (!email) return;
        try {
            this.confirmLoading = true;
            const res = await userAPI.confirm(email, code);
            if (res.result === 'success') {
                runInAction(() => {
                    this.confirmLoading = false;
                    this.confirmError = false;
                    this.confirmed = true;
                    this.operationSuccess = true;
                });
            } else if (
                res.result === 'error' &&
                res.info.indexOf('wrong_code') >= 0
            ) {
                runInAction(() => {
                    this.confirmLoading = false;
                    this.confirmError = true;
                });
            } else {
                runInAction(() => {
                    this.confirmLoading = false;
                    this.serverError = true;
                });
            }
        } catch (e) {
            runInAction(() => {
                this.confirmLoading = false;
                this.serverError = true;
            });
        }
    }

    @action
    async requestConfirm() {
        const email = this.email;
        if (!email) return;
        try {
            this.loading = true;
            const res = await userAPI.requestConfirm(email);
            if (res.result === 'success') {
                runInAction(() => {
                    this.sendedAgain = true;
                    this.loading = false;
                });
                this.startTimer();
            } else {
                runInAction(() => {
                    this.loading = false;
                    this.serverError = true;
                });
            }
        } catch (e) {
            runInAction(() => {
                this.loading = false;
                this.serverError = true;
            });
        }
    }

    @action
    async passwordRecovery(email: string) {
        this.passwordRecoveryLoading = true;
        try {
            const res = await userAPI.passwordRecovery(email);
            if (res.result === 'success') {
                runInAction(() => {
                    this.sendedAgain = true;
                    this.passwordRecoveryLoading = false;
                    this.authError = false;
                    this.email = email;
                });
                this.startTimer();
            } else if (
                res.result === 'error' &&
                res.info.indexOf('not_exists_user') >= 0
            ) {
                runInAction(() => {
                    this.passwordRecoveryLoading = false;
                    this.notExistError = true;
                });
            } else {
                runInAction(() => {
                    this.passwordRecoveryLoading = false;
                    this.serverError = true;
                });
            }
        } catch (e) {
            runInAction(() => {
                this.passwordRecoveryLoading = false;
                this.serverError = true;
            });
        }
    }

    @action
    async confirmPasswordRecovery(
        email: string,
        password: string,
        code: string,
    ) {
        const isDataValid = this._checkAuth(email, password);
        if (isDataValid) {
            this.confirmPasswordRecoveryLoading = true;
            try {
                const res = await userAPI.confirmPasswordRecovery(
                    email,
                    password,
                    code,
                );
                if (res.result === 'success') {
                    runInAction(() => {
                        this.confirmPasswordRecoveryLoading = false;
                        this.passwordConfirmed = true;
                        this.confirmError = false;
                    });
                } else if (
                    res.result === 'error' &&
                    res.info.indexOf('wrong_code') >= 0
                ) {
                    runInAction(() => {
                        this.confirmPasswordRecoveryLoading = false;
                        this.confirmError = true;
                    });
                } else {
                    runInAction(() => {
                        this.confirmPasswordRecoveryLoading = false;
                        this.serverError = true;
                    });
                }
            } catch (e) {
                runInAction(() => {
                    this.confirmPasswordRecoveryLoading = false;
                    this.serverError = true;
                });
            }
        }
    }

    @action
    async getSettings() {
        const token = this.token;
        if (!token) return;
        try {
            this.loading = true;
            const res = await userAPI.getSettings(token);
            if (res.result === 'success') {
                runInAction(() => {
                    this.loading = false;
                    this.settings = res.settings;
                });
            } else if (
                res.result === 'error' &&
                res.info.indexOf('not_auth') >= 0
            ) {
                runInAction(() => {
                    this.loading = false;
                    this.authError = true;
                    this.email = null;
                    this.token = '';
                    this.type = '';
                });
                localStorage.setItem('groupId', '');
                localStorage.setItem('selectedDevice', '');
                localStorage.setItem('token', '');
                localStorage.setItem('userType', '');
                localStorage.setItem('userLogin', '');
            } else {
                runInAction(() => {
                    this.loading = false;
                    this.serverError = true;
                });
            }
        } catch (e) {
            runInAction(() => {
                this.loading = false;
                this.serverError = true;
            });
        }
    }

    @action
    async changeSettings(settings: Partial<IUserSettings>) {
        const token = this.token;
        if (!token) return;
        try {
            this.changeSettingsLoading = true;
            const res = await userAPI.changeSettings(token, settings);
            if (res.result === 'success') {
                runInAction(() => {
                    this.changeSettingsLoading = false;
                    this.changeSettingsSuccess = true;
                    this.getSettings();
                });
            } else {
                runInAction(() => {
                    this.changeSettingsLoading = false;
                    this.serverError = true;
                });
            }
        } catch (e) {
            runInAction(() => {
                this.changeSettingsLoading = false;
                this.serverError = true;
            });
        }
    }

    @action
    setUserLogin(login: string) {
        this.userLogin = login;
        localStorage.setItem('userLogin', login);
    }

    @action
    resetAuthError() {
        this.authError = false;
    }

    @action
    resetConfirmError() {
        this.confirmError = false;
    }

    @action
    resetRegisterError() {
        this.registerError = false;
    }

    @action
    resetServerError() {
        this.serverError = false;
    }

    @action
    resetSendAlert() {
        this.sendedAgain = false;
    }

    @action
    setConfirmed() {
        this.confirmed = true;
    }

    @action
    resetConfirmed() {
        this.confirmed = false;
    }

    @action
    resetPasswordConfirmed() {
        this.passwordConfirmed = false;
    }

    @action
    resetNotExistError() {
        this.notExistError = false;
    }

    @action
    resetEmail() {
        this.email = '';
    }

    @action
    resetChangeSettingsSuccess() {
        this.changeSettingsSuccess = false;
    }

    @action
    resetOperationSuccess() {
        this.operationSuccess = false;
    }

    @action
    setConfirmationErr() {
        this.confirmationErr = true;
    }

    @action
    resetConfirmationErr() {
        this.confirmationErr = false;
    }
}
